import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import React, { ErrorInfo } from 'react';
import styled from 'styled-components/macro';
import { ConfigContext, ConfigContextValue } from './config/ConfigContext';
import { PageContainer } from './layout/Layout';
import { SecondaryButton } from './shared/Button';
import { Header1 } from './shared/Headers';
import { colourNeutral10 } from './styling/colours';
import { spacing04, spacing05 } from './styling/spacing';

type Props = {
  children?: React.ReactNode;
  logErrorsToApplicationInsights?: boolean;
};

type State = {
  details: { error: Error; errorInfo: ErrorInfo } | null;
  showDetails: boolean;
};

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { details: null, showDetails: false };

  static contextType = ConfigContext;
  context!: ConfigContextValue;

  appInsights: ApplicationInsights | null = null;

  componentDidMount() {
    if (this.props.logErrorsToApplicationInsights) {
      const appInsightsConnectionString = this.context.getConfig().applicationInsights.connectionString;

      if (!appInsightsConnectionString) {
        return;
      }

      this.appInsights = new ApplicationInsights({
        config: {
          connectionString: appInsightsConnectionString,
        },
      });

      this.appInsights.loadAppInsights();
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ details: { error, errorInfo } });

    if (this.appInsights != null) {
      this.appInsights.trackException({ exception: error });
      this.appInsights.flush();
    }
  }

  toggleShowDetails = () => this.setState(state => ({ showDetails: !state.showDetails }));

  render() {
    if (this.state.details != null) {
      return (
        <PageContainer>
          <Header1>Sorry, something went wrong</Header1>
          <p>An unexpected error has caused the application to crash.</p>
          <p>Please refresh the page and try again.</p>
          <ToggleDetailsButton onClick={this.toggleShowDetails}>
            {this.state.showDetails ? 'Hide' : 'Show'} details
          </ToggleDetailsButton>
          {this.state.showDetails && (
            <DetailsPanel data-testid={errorBoundaryDetailsPaneTestId}>
              <pre>{this.state.details.error.toString()}</pre>
              <pre>{this.state.details.errorInfo.componentStack}</pre>
            </DetailsPanel>
          )}
        </PageContainer>
      );
    }

    return this.props.children;
  }
}

const ToggleDetailsButton = styled(SecondaryButton)`
  margin-top: ${spacing04};
`;

const DetailsPanel = styled.div`
  padding: ${spacing05};
  margin-top: ${spacing04};
  background-color: ${colourNeutral10};
`;

export const errorBoundaryDetailsPaneTestId = 'error-boundary-details-pane';
